// Pure numbers behind the Case dashboard's Live session view: per-player
// status and impact summaries, session totals, and the alerts worth a coach's
// attention. No React, no i18n — the view turns these into words.

import { parseUtc, HEARD_OK_MS, HEARD_WARN_MS } from "utils/caseDashboard";

// Impact severity bands (g), shared with the wearable tools' impactVisuals:
// light < 10 ≤ moderate < 25 ≤ hard < 50 ≤ severe.
export const SEVERITY_BANDS = [10, 25, 50];
export const SEVERITY_LEVELS = ["light", "moderate", "hard", "severe"];
// Fills (dots, bars) and the darker shade used for numbers in text, so the
// yellow step stays readable on white.
export const SEVERITY_COLORS = ["#22C55E", "#EAB308", "#F97316", "#DC2626"];
export const SEVERITY_TEXT = ["#15803D", "#A16207", "#C2410C", "#B91C1C"];

export const LOW_BATTERY_PCT = 20;
// A session this old is probably one nobody ended.
export const LONG_SESSION_MS = 4 * 60 * 60 * 1000;

/** 0..3 index into SEVERITY_LEVELS for a peak in g. */
export function severityIndex(g) {
  const v = Number(g);
  if (!(v >= SEVERITY_BANDS[0])) return 0;
  if (v < SEVERITY_BANDS[1]) return 1;
  if (v < SEVERITY_BANDS[2]) return 2;
  return 3;
}

// Distinct colours handed out in roster order, so 12 players never share one.
const PALETTE = [
  "#E07A3A",
  "#2563EB",
  "#059669",
  "#7C3AED",
  "#DB2777",
  "#0891B2",
  "#CA8A04",
  "#4F46E5",
  "#65A30D",
  "#DC2626",
  "#0D9488",
  "#9333EA",
  "#EA580C",
  "#0284C7",
  "#16A34A",
  "#C026D3",
];

export const playerKeyOf = (row) => row.player_id || row.mac_address;

/** "live" (heard ≤ 1 min), "quiet" (≤ 5 min), "silent", or "never". */
export function wearableStatus(lastContact, now) {
  const d = parseUtc(lastContact);
  if (!d) return "never";
  const age = now - d.getTime();
  if (age <= HEARD_OK_MS) return "live";
  if (age <= HEARD_WARN_MS) return "quiet";
  return "silent";
}

/** Impacts per bucket across [from, to], for sparklines. */
export function bucketCounts(times, from, to, buckets) {
  const out = new Array(buckets).fill(0);
  const span = Math.max(1, to - from);
  for (const t of times) {
    if (t < from || t > to) continue;
    const i = Math.min(buckets - 1, Math.floor(((t - from) / span) * buckets));
    out[i] += 1;
  }
  return out;
}

/**
 * Everything the Live view shows, from the polled roster and impacts.
 * `now` is "the moment we're describing": the clock for a live session, the
 * session's end for a replay.
 */
export function liveStats({ session, roster, impacts, now, replay = false }) {
  const started = parseUtc(session?.started_at)?.getTime() ?? null;
  const ended = parseUtc(session?.ended_at)?.getTime() ?? null;
  const end = replay ? ended ?? now : now;

  const players = roster.map((r, i) => ({
    key: playerKeyOf(r),
    id: r.id,
    name: r.player_name || null,
    mac: r.mac_address,
    wearable: r.wearable_id || null,
    color: PALETTE[i % PALETTE.length],
    removed: !!r.removed_at,
    battery: r.battery_level == null ? null : Number(r.battery_level),
    lastContact: parseUtc(r.last_contact_time)?.getTime() ?? null,
    status: replay ? "ended" : wearableStatus(r.last_contact_time, now),
    count: 0,
    maxG: 0,
    maxAt: null,
    lastImpactAt: null,
    severity: [0, 0, 0, 0],
    times: [],
  }));
  const byKey = new Map(players.map((p) => [p.key, p]));

  const severity = [0, 0, 0, 0];
  let biggest = null;
  let last = null;
  const times = [];
  const points = [];
  for (const imp of impacts) {
    const t = parseUtc(imp.time)?.getTime();
    if (t == null) continue;
    const g = Number(imp.linear_force) || 0;
    const sev = severityIndex(g);
    const key = playerKeyOf(imp);
    const p = byKey.get(key);
    severity[sev] += 1;
    times.push(t);
    points.push({
      id: imp.id,
      t,
      g,
      sev,
      key,
      name: p?.name || imp.player_name || null,
      color: p?.color || "#9CA3AF",
    });
    if (!biggest || g > biggest.g)
      biggest = { g, t, key, name: p?.name || imp.player_name || null };
    if (!last || t >= last.t)
      last = { g, t, key, name: p?.name || imp.player_name || null };
    if (p) {
      p.count += 1;
      p.severity[sev] += 1;
      p.times.push(t);
      if (g > p.maxG) {
        p.maxG = g;
        p.maxAt = t;
      }
      if (!p.lastImpactAt || t > p.lastImpactAt) p.lastImpactAt = t;
    }
  }

  const active = players.filter((p) => !p.removed);
  const statusCounts = { live: 0, quiet: 0, silent: 0, never: 0 };
  for (const p of active)
    if (statusCounts[p.status] != null) statusCounts[p.status] += 1;

  const recentFrom = end - 5 * 60 * 1000;
  const recent = times.filter((t) => t >= recentFrom).length;
  // Sparkline: the last 30 minutes (or the whole session if shorter), 30 bars.
  const sparkFrom = Math.max(
    started ?? end - 30 * 60 * 1000,
    end - 30 * 60 * 1000
  );
  const sparkline = bucketCounts(times, sparkFrom, end, 30);

  const lastHeardAny =
    active.reduce((m, p) => Math.max(m, p.lastContact || 0), 0) || null;

  return {
    started,
    ended,
    end,
    durationMs: started != null ? end - started : null,
    players,
    active,
    statusCounts,
    total: points.length,
    recent,
    perMinute: recent / 5,
    sparkline,
    sparkFrom,
    severity,
    biggest,
    last,
    points,
    lastHeardAny,
    alerts: replay ? [] : alertsFor({ active, started, now, lastHeardAny }),
  };
}

/**
 * What needs attention, most urgent first. Each alert is
 * { id, tone: "bad"|"warn"|"info", kind, players?, ms?, battery? } — the view
 * words it.
 */
export function alertsFor({ active, started, now, lastHeardAny }) {
  const alerts = [];
  if (active.length && (!lastHeardAny || now - lastHeardAny > HEARD_WARN_MS)) {
    alerts.push({
      id: "none-heard",
      tone: "bad",
      kind: "noneHeard",
      ms: lastHeardAny ? now - lastHeardAny : null,
    });
  } else {
    const silent = active.filter(
      (p) => p.status === "silent" || p.status === "never"
    );
    if (silent.length)
      alerts.push({
        id: "silent",
        tone: "bad",
        kind: "silent",
        players: silent,
      });
    const quiet = active.filter((p) => p.status === "quiet");
    if (quiet.length)
      alerts.push({ id: "quiet", tone: "warn", kind: "quiet", players: quiet });
  }
  const low = active.filter(
    (p) => p.battery != null && p.battery < LOW_BATTERY_PCT
  );
  if (low.length)
    alerts.push({ id: "battery", tone: "warn", kind: "battery", players: low });
  if (started != null && now - started > LONG_SESSION_MS)
    alerts.push({
      id: "long",
      tone: "info",
      kind: "longSession",
      ms: now - started,
    });
  return alerts;
}

/** Points within the chosen window ("5m" | "15m" | "1h" | "all"). */
export const RANGES = {
  "5m": 5 * 60e3,
  "15m": 15 * 60e3,
  "1h": 60 * 60e3,
  all: null,
};
export function rangeStart(range, stats) {
  const span = RANGES[range];
  if (!span) return stats.started ?? stats.points[0]?.t ?? stats.end;
  return Math.max(stats.end - span, stats.started ?? -Infinity);
}

/**
 * Average a time series into at most `buckets` points over [from, to], so a
 * 48-hour chart draws ~200 points instead of 5,760 raw 30-second rows.
 * `rows` are { x (ms), ...numbers }. Means for `fields`, the max for
 * `maxFields`; `session` is true when any row in the bucket had a session.
 */
export function downsample(
  rows,
  { from, to, buckets, fields, maxFields = [] }
) {
  const span = Math.max(1, to - from);
  const acc = new Map();
  for (const r of rows) {
    if (r.x < from || r.x > to) continue;
    const i = Math.min(
      buckets - 1,
      Math.floor(((r.x - from) / span) * buckets)
    );
    let b = acc.get(i);
    if (!b) {
      b = {
        n: {},
        sum: {},
        max: {},
        session: false,
        statuses: new Set(),
        issues: new Set(),
      };
      acc.set(i, b);
    }
    for (const f of fields) {
      const v = Number(r[f]);
      if (r[f] == null || Number.isNaN(v)) continue;
      b.sum[f] = (b.sum[f] || 0) + v;
      b.n[f] = (b.n[f] || 0) + 1;
    }
    for (const f of maxFields) {
      const v = Number(r[f]);
      if (r[f] == null || Number.isNaN(v)) continue;
      b.max[f] = Math.max(b.max[f] ?? -Infinity, v);
    }
    if (r.session_id) b.session = true;
    if (r.status) b.statuses.add(r.status);
    if (r.issues)
      String(r.issues)
        .split(",")
        .forEach((c) => c && b.issues.add(c));
  }
  const out = [];
  for (const [i, b] of [...acc.entries()].sort((a, c) => a[0] - c[0])) {
    const p = { x: from + ((i + 0.5) / buckets) * span, session: b.session };
    for (const f of fields)
      p[f] = b.n[f] ? Math.round((b.sum[f] / b.n[f]) * 100) / 100 : null;
    for (const f of maxFields) p[`${f}_max`] = b.max[f] ?? null;
    p.status = b.statuses.has("critical")
      ? "critical"
      : b.statuses.has("warn")
      ? "warn"
      : b.statuses.size
      ? "ok"
      : null;
    p.issues = [...b.issues];
    out.push(p);
  }
  return out;
}
